import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "../components/ui/tabs";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import {
  Plus, Trash, PencilSimple, Spinner, Receipt, House,
  CheckCircle, Clock, Warning, Lightning, ArrowCounterClockwise,
  ShieldCheck, CalendarBlank, CaretDown, MagnifyingGlass, X, Funnel,
  DownloadSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { getErrorMessage } from "../lib/utils";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import Pagination from "../components/Pagination";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FixedExpensesPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [types, setTypes] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [configs, setConfigs] = useState({});
  const [exemptions, setExemptions] = useState({});
  const [invoices, setInvoices] = useState([]);
  const [units, setUnits] = useState({});

  // Type dialog
  const [typeDialog, setTypeDialog] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [typeName, setTypeName] = useState("");
  const [typeDesc, setTypeDesc] = useState("");
  const [typeSaving, setTypeSaving] = useState(false);

  // Config dialog
  const [configDialog, setConfigDialog] = useState(false);
  const [configApt, setConfigApt] = useState(null);
  const [configTypeId, setConfigTypeId] = useState("");
  const [configAmount, setConfigAmount] = useState(0);
  const [configDueDay, setConfigDueDay] = useState(10);
  const [configDueHour, setConfigDueHour] = useState(8);
  const [configSaving, setConfigSaving] = useState(false);

  // Selected apartment for detail
  const [selectedApt, setSelectedApt] = useState(null);
  // Invoice filters
  const [invoiceAptFilter, setInvoiceAptFilter] = useState("all");
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState("all");
  const [invoicePage, setInvoicePage] = useState(1);
  const [invoicePageSize, setInvoicePageSize] = useState(20);
  // Delete Invoice dialog
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Manual Invoice dialog
  const [manualDialog, setManualDialog] = useState(false);
  const [manualAptId, setManualAptId] = useState("");
  const [manualUnitId, setManualUnitId] = useState("");
  const [manualDesc, setManualDesc] = useState("");
  const [manualAmount, setManualAmount] = useState(0);
  const [manualDueDate, setManualDueDate] = useState("");
  const [manualSaving, setManualSaving] = useState(false);
  const [manualUnits, setManualUnits] = useState([]);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [typesRes, aptsRes, invoicesRes] = await Promise.all([
        axios.get(`${API_URL}/api/fixed-expenses/types`),
        axios.get(`${API_URL}/api/apartments`),
        axios.get(`${API_URL}/api/fixed-expenses/invoices`),
      ]);
      setTypes(typesRes.data);
      setApartments(aptsRes.data);
      setInvoices(invoicesRes.data);
      // Load configs and units for each apartment
      const cfgs = {};
      const exempts = {};
      const unitMap = {};
      for (const apt of aptsRes.data) {
        const [cfgRes, exRes, uRes] = await Promise.all([
          axios.get(`${API_URL}/api/fixed-expenses/configs/${apt.id}`),
          axios.get(`${API_URL}/api/fixed-expenses/exemptions/${apt.id}`),
          axios.get(`${API_URL}/api/apartments/${apt.id}/units`),
        ]);
        cfgs[apt.id] = cfgRes.data;
        exempts[apt.id] = exRes.data;
        unitMap[apt.id] = uRes.data;
      }
      setConfigs(cfgs);
      setExemptions(exempts);
      setUnits(unitMap);
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setLoading(false); }
  };

  // ─── Type CRUD ───
  const openTypeDialog = (type = null) => {
    setEditingType(type);
    setTypeName(type?.name || "");
    setTypeDesc(type?.description || "");
    setTypeDialog(true);
  };

  const saveType = async () => {
    if (!typeName.trim()) { toast.error("Gider adi gerekli"); return; }
    setTypeSaving(true);
    try {
      if (editingType) {
        await axios.put(`${API_URL}/api/fixed-expenses/types/${editingType.id}`, { name: typeName, description: typeDesc });
        toast.success("Gider turu guncellendi");
      } else {
        await axios.post(`${API_URL}/api/fixed-expenses/types`, { name: typeName, description: typeDesc });
        toast.success("Gider turu eklendi");
      }
      setTypeDialog(false);
      fetchAll();
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setTypeSaving(false); }
  };

  const deleteType = async (id) => {
    if (!window.confirm("Bu gider turunu silmek istediginize emin misiniz?")) return;
    try {
      await axios.delete(`${API_URL}/api/fixed-expenses/types/${id}`);
      toast.success("Gider turu silindi");
      fetchAll();
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  // ─── Config ───
  const openConfigDialog = (apt, typeId = "") => {
    setConfigApt(apt);
    setConfigTypeId(typeId);
    const existing = (configs[apt.id] || []).find(c => c.expense_type_id === typeId);
    setConfigAmount(existing?.amount || 0);
    setConfigDueDay(existing?.due_day || 10);
    setConfigDueHour(existing?.due_hour || 8);
    setConfigDialog(true);
  };

  const saveConfig = async () => {
    if (!configTypeId || !configApt) return;
    setConfigSaving(true);
    try {
      await axios.post(`${API_URL}/api/fixed-expenses/configs`, {
        apartment_id: configApt.id,
        expense_type_id: configTypeId,
        amount: configAmount,
        due_day: configDueDay,
        due_hour: configDueHour,
      });
      toast.success("Yapilandirma kaydedildi");
      setConfigDialog(false);
      fetchAll();
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setConfigSaving(false); }
  };

  // ─── Exemptions ───
  const toggleExemption = async (unitId, expTypeId, aptId, currentExempt) => {
    try {
      await axios.post(`${API_URL}/api/fixed-expenses/exemptions`, {
        unit_id: unitId,
        expense_type_id: expTypeId,
        is_exempt: !currentExempt,
      });
      toast.success(currentExempt ? "Muafiyet kaldirildi" : "Muafiyet verildi");
      fetchAll();
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  // ─── Invoice actions ───
  const bypassInvoice = async (invoiceId) => {
    try {
      await axios.post(`${API_URL}/api/fixed-expenses/invoices/${invoiceId}/bypass`);
      toast.success("Fatura bypass edildi");
      fetchAll();
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  const unbypassInvoice = async (invoiceId) => {
    try {
      await axios.post(`${API_URL}/api/fixed-expenses/invoices/${invoiceId}/unbypass`);
      toast.success("Bypass kaldirildi");
      fetchAll();
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  const approveInvoice = async (invoiceId) => {
    try {
      await axios.post(`${API_URL}/api/fixed-expenses/invoices/${invoiceId}/approve`);
      toast.success("Odeme onaylandi");
      fetchAll();
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  const generateInvoices = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/fixed-expenses/generate-invoices`);
      toast.success(`${res.data.invoices_created} fatura olusturuldu (${res.data.period})`);
      fetchAll();
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  // ─── Manual Invoice ───
  const openManualDialog = () => {
    setManualAptId("");
    setManualUnitId("");
    setManualDesc("");
    setManualAmount(0);
    const defaultDue = new Date();
    defaultDue.setDate(defaultDue.getDate() + 10);
    setManualDueDate(defaultDue.toISOString().split("T")[0]);
    setManualUnits([]);
    setManualDialog(true);
  };

  const onManualAptChange = (aptId) => {
    setManualAptId(aptId);
    setManualUnitId("");
    setManualUnits(units[aptId] || []);
  };

  const saveManualInvoice = async () => {
    if (!manualUnitId || !manualDesc.trim() || manualAmount <= 0) {
      toast.error("Daire, aciklama ve gecerli tutar gereklidir");
      return;
    }
    setManualSaving(true);
    try {
      await axios.post(`${API_URL}/api/fixed-expenses/invoices/manual`, {
        unit_id: manualUnitId,
        description: manualDesc,
        amount: manualAmount,
        due_date: manualDueDate ? new Date(manualDueDate).toISOString() : undefined,
      });
      toast.success("Fatura olusturuldu");
      setManualDialog(false);
      fetchAll();
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setManualSaving(false); }
  };

  const downloadInvoicePdf = async (invoiceId) => {
    try {
      const res = await axios.get(`${API_URL}/api/fixed-expenses/invoices/${invoiceId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `fatura-${invoiceId.slice(0, 8)}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("PDF indirme basarisiz"); }
  };

  const isExempt = (aptId, unitId, expTypeId) => {
    return (exemptions[aptId] || []).some(e => e.unit_id === unitId && e.expense_type_id === expTypeId);
  };

  const statusBadge = (status) => {
    const map = {
      pending: { cls: "bg-amber-100 text-amber-700", label: t("fixed_expenses.pending") },
      overdue: { cls: "bg-red-100 text-red-700", label: t("fixed_expenses.overdue") },
      paid: { cls: "bg-emerald-100 text-emerald-700", label: t("fixed_expenses.paid") },
      bypassed: { cls: "bg-slate-100 text-slate-500", label: t("fixed_expenses.bypass") },
      pending_approval: { cls: "bg-blue-100 text-blue-700", label: t("dashboard.waiting_approval") },
    };
    const s = map[status] || { cls: "bg-slate-100", label: status };
    return <Badge className={s.cls}>{s.label}</Badge>;
  };

  const canDeleteInvoice = user?.role === "admin" || user?.role === "customer" ||
    (user?.role === "staff" && user?.permissions?.invoice_delete);

  const handleDeleteInvoice = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API_URL}/api/fixed-expenses/invoices/${deleteTarget.id}`);
      toast.success(t("invoices.deleted_success"));
      setInvoices(prev => prev.filter(inv => inv.id !== deleteTarget.id));
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDeleteDialog(false);
      setDeleteTarget(null);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Spinner size={32} className="animate-spin text-indigo-600" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="fixed-expenses-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">{t("fixed_expenses.title")}</h1>
          <p className="text-slate-500 mt-1">{t("fixed_expenses.subtitle")}</p>
        </div>
        <Button size="sm" variant="outline" className="text-indigo-600 border-indigo-200"
          onClick={generateInvoices} data-testid="generate-invoices-btn">
          <Receipt size={14} className="mr-1" /> {t("fixed_expenses.generate")}
        </Button>
      </div>

      <Tabs defaultValue="types" className="w-full">
        <TabsList className="bg-slate-100 w-full sm:w-auto overflow-x-auto">
          <TabsTrigger value="types" data-testid="tab-types">{t("fixed_expenses.types")}</TabsTrigger>
          <TabsTrigger value="apartments" data-testid="tab-apartments">{t("fixed_expenses.apartment_settings")}</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices">{t("fixed_expenses.invoices_tab")}</TabsTrigger>
        </TabsList>

        {/* ─── Tab: Expense Types ─── */}
        <TabsContent value="types" className="mt-4">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base font-heading">{t("fixed_expenses.types")}</CardTitle>
              <Button size="sm" onClick={() => openTypeDialog()} data-testid="add-type-btn"
                className="bg-indigo-600 hover:bg-indigo-700 text-white">
                <Plus size={14} className="mr-1" /> {t("fixed_expenses.new_type")}
              </Button>
            </CardHeader>
            <CardContent>
              {types.length === 0 ? (
                <p className="text-center py-8 text-slate-400">{t("fixed_expenses.no_type")}</p>
              ) : (
                <div className="space-y-3">
                  {types.map(expType => (
                    <div key={expType.id} className="flex items-center justify-between p-3 rounded-lg border border-slate-200" data-testid={`type-${expType.id}`}>
                      <div>
                        <span className="font-medium text-slate-900">{expType.name}</span>
                        {expType.description && <span className="text-sm text-slate-400 ml-2">{expType.description}</span>}
                      </div>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openTypeDialog(expType)}>
                          <PencilSimple size={14} />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => deleteType(expType.id)}>
                          <Trash size={14} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Tab: Apartment Configs ─── */}
        <TabsContent value="apartments" className="mt-4 space-y-4">
          {apartments.map(apt => (
            <Card key={apt.id} className="border-slate-200 shadow-sm" data-testid={`apt-config-${apt.id}`}>
              <CardHeader className="pb-2 cursor-pointer" onClick={() => setSelectedApt(selectedApt?.id === apt.id ? null : apt)}>
                <CardTitle className="text-base font-heading flex items-center gap-2">
                  <House size={16} className="text-indigo-600" /> {apt.name}
                  <Badge className="bg-slate-100 text-slate-500 text-xs">{(units[apt.id] || []).length} {t("fixed_expenses.unit_label")}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Configured expenses */}
                {types.map(type => {
                  const cfg = (configs[apt.id] || []).find(c => c.expense_type_id === type.id);
                  return (
                    <div key={type.id} className="flex items-center justify-between p-2 rounded border border-slate-100 bg-slate-50/50">
                      <div className="flex items-center gap-2">
                        <Receipt size={14} className="text-slate-400" />
                        <span className="text-sm font-medium">{type.name}</span>
                        {cfg ? (
                          <>
                            <Badge className="bg-emerald-100 text-emerald-700">{cfg.amount} TL</Badge>
                            <span className="text-xs text-slate-400">Son odeme: {cfg.due_day}. gun, {String(cfg.due_hour).padStart(2,'0')}:{String(cfg.due_minute || 0).padStart(2,'0')}</span>
                          </>
                        ) : (
                          <span className="text-xs text-slate-400">{t("fixed_expenses.not_configured")}</span>
                        )}
                      </div>
                      <Button size="sm" variant="outline" className="h-7 text-xs"
                        onClick={() => openConfigDialog(apt, type.id)} data-testid={`config-${apt.id}-${type.id}`}>
                        {cfg ? <PencilSimple size={12} className="mr-1" /> : <Plus size={12} className="mr-1" />}
                        {cfg ? t("common.edit") : t("fixed_expenses.configure")}
                      </Button>
                    </div>
                  );
                })}

                {/* Unit exemptions (expanded) */}
                {selectedApt?.id === apt.id && (
                  <div className="mt-3 p-3 bg-white rounded-lg border border-slate-200">
                    <h4 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-1">
                      <ShieldCheck size={14} className="text-indigo-600" /> {t("fixed_expenses.exemptions")}
                    </h4>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead className="text-xs">{t("common.unit")}</TableHead>
                            {types.map(expType => (
                              <TableHead key={expType.id} className="text-xs text-center">{expType.name}</TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(units[apt.id] || []).map(unit => (
                            <TableRow key={unit.id}>
                              <TableCell className="text-sm font-medium">{unit.name}</TableCell>
                              {types.map(expType => {
                                const exempt = isExempt(apt.id, unit.id, expType.id);
                                return (
                                  <TableCell key={expType.id} className="text-center">
                                    <Switch checked={exempt}
                                      onCheckedChange={() => toggleExemption(unit.id, expType.id, apt.id, exempt)}
                                      data-testid={`exempt-${unit.id}-${expType.id}`} />
                                    <span className="text-[10px] block text-slate-400 mt-0.5">
                                      {exempt ? t("fixed_expenses.exempt") : t("common.active")}
                                    </span>
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* ─── Tab: Invoices (Accordion by Period) ─── */}
        <TabsContent value="invoices" className="mt-4 space-y-4">
          {/* Filters + Manual Invoice Button */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Funnel size={16} className="text-slate-400" />
                <Select value={invoiceAptFilter} onValueChange={setInvoiceAptFilter}>
                  <SelectTrigger className="w-48" data-testid="invoice-apt-filter">
                    <SelectValue placeholder="Apartman" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("fixed_expenses.all_apartments")}</SelectItem>
                    {apartments.map(a => (
                      <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Select value={invoiceStatusFilter} onValueChange={setInvoiceStatusFilter}>
                <SelectTrigger className="w-40" data-testid="invoice-status-filter">
                  <SelectValue placeholder={t("common.status")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("fixed_expenses.all_statuses")}</SelectItem>
                  <SelectItem value="pending">{t("fixed_expenses.pending")}</SelectItem>
                  <SelectItem value="paid">{t("fixed_expenses.paid")}</SelectItem>
                  <SelectItem value="bypassed">{t("fixed_expenses.bypass")}</SelectItem>
                  <SelectItem value="overdue">{t("fixed_expenses.overdue")}</SelectItem>
                </SelectContent>
              </Select>
              {(invoiceAptFilter !== "all" || invoiceStatusFilter !== "all") && (
                <Button variant="ghost" size="sm" onClick={() => { setInvoiceAptFilter("all"); setInvoiceStatusFilter("all"); }}
                  className="text-slate-500 hover:text-slate-700" data-testid="invoice-clear-filters">
                  <X size={14} className="mr-1" /> Temizle
                </Button>
              )}
            </div>
            <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white"
              onClick={openManualDialog} data-testid="add-manual-invoice-btn">
              <Plus size={14} className="mr-1" /> Tek Fatura Ekle
            </Button>
          </div>

          {(() => {
            // Filter invoices
            const filtered = invoices.filter(inv => {
              if (invoiceAptFilter !== "all" && inv.apartment_id !== invoiceAptFilter) return false;
              if (invoiceStatusFilter !== "all" && inv.status !== invoiceStatusFilter) return false;
              return true;
            });
            // Group by period (sorted newest first)
            const grouped = {};
            filtered.forEach(inv => {
              const period = inv.period || "Bilinmiyor";
              if (!grouped[period]) grouped[period] = [];
              grouped[period].push(inv);
            });
            const periods = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

            if (periods.length === 0) {
              return (
                <Card className="border-slate-200 shadow-sm">
                  <CardContent className="py-12 text-center">
                    <Receipt size={48} className="mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-400">{t("fixed_expenses.no_invoices")}</p>
                  </CardContent>
                </Card>
              );
            }

            return (
              <>
              <Accordion type="single" collapsible className="space-y-2" data-testid="invoice-accordion">
                {periods.slice((invoicePage - 1) * invoicePageSize, invoicePage * invoicePageSize).map(period => {
                  const items = grouped[period];
                  const totalAmount = items.reduce((s, i) => s + (i.amount || 0), 0);
                  const paidCount = items.filter(i => i.status === "paid").length;
                  const pendingCount = items.filter(i => ["pending", "overdue"].includes(i.status)).length;
                  const bypassCount = items.filter(i => i.status === "bypassed").length;

                  return (
                    <AccordionItem key={period} value={period}
                      className="border border-slate-200 rounded-lg px-4 shadow-sm bg-white overflow-hidden"
                      data-testid={`period-${period}`}>
                      <AccordionTrigger className="hover:no-underline py-3.5">
                        <div className="flex items-center gap-3 flex-wrap w-full pr-2">
                          <div className="flex items-center gap-2">
                            <CalendarBlank size={18} className="text-indigo-600" />
                            <span className="font-bold text-slate-900 text-base">{period}</span>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap ml-auto mr-4">
                            <Badge variant="outline" className="text-xs">{items.length} {t("fixed_expenses.invoice_label")}</Badge>
                            <Badge className="bg-indigo-50 text-indigo-700 text-xs font-bold">{totalAmount.toFixed(0)} TL</Badge>
                            {paidCount > 0 && <Badge className="bg-emerald-100 text-emerald-700 text-xs">{paidCount} {t("fixed_expenses.paid")}</Badge>}
                            {pendingCount > 0 && <Badge className="bg-amber-100 text-amber-700 text-xs">{pendingCount} {t("fixed_expenses.pending")}</Badge>}
                            {bypassCount > 0 && <Badge className="bg-slate-100 text-slate-500 text-xs">{bypassCount} {t("fixed_expenses.bypass")}</Badge>}
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="overflow-x-auto -mx-1">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-slate-50">
                                <TableHead className="text-xs">{t("common.unit")}</TableHead>
                                <TableHead className="text-xs">{t("common.tenant")}</TableHead>
                                <TableHead className="text-xs">{t("common.expense")}</TableHead>
                                <TableHead className="text-xs">{t("common.amount")}</TableHead>
                                <TableHead className="text-xs">{t("common.due_date")}</TableHead>
                                <TableHead className="text-xs">{t("common.status")}</TableHead>
                                <TableHead className="text-xs">{t("common.actions")}</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {items.map(inv => (
                                <TableRow key={inv.id} data-testid={`inv-row-${inv.id}`}
                                  className={inv.electricity_cut ? "bg-red-50/50" : ""}>
                                  <TableCell className="text-sm font-medium">{inv.unit_name}</TableCell>
                                  <TableCell className="text-sm">{inv.tenant_name || "-"}</TableCell>
                                  <TableCell><Badge className="bg-indigo-50 text-indigo-700 text-xs">{inv.expense_type_name}</Badge></TableCell>
                                  <TableCell className="font-bold text-sm">{inv.amount.toFixed(2)} TL</TableCell>
                                  <TableCell className="text-xs text-slate-500">
                                    {inv.due_date ? new Date(inv.due_date).toLocaleDateString("tr-TR") : "-"}
                                  </TableCell>
                                  <TableCell>
                                    {statusBadge(inv.status)}
                                    {inv.electricity_cut && (
                                      <Badge className="bg-red-100 text-red-700 text-[10px] ml-1">{t("fixed_expenses.cutoff")}</Badge>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex gap-1 flex-wrap">
                                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-indigo-600"
                                        onClick={() => downloadInvoicePdf(inv.id)} data-testid={`pdf-${inv.id}`}
                                        title={t("common.pdf")}>
                                        <DownloadSimple size={14} />
                                      </Button>
                                      {inv.status === "pending_approval" && (
                                        <Button size="sm" variant="outline" className="h-7 text-xs text-emerald-600 border-emerald-200"
                                          onClick={() => approveInvoice(inv.id)} data-testid={`approve-${inv.id}`}>
                                          <CheckCircle size={12} className="mr-1" /> {t("fixed_expenses.approve")}
                                        </Button>
                                      )}
                                      {["pending", "overdue"].includes(inv.status) && (
                                        <Button size="sm" variant="outline" className="h-7 text-xs text-slate-500"
                                          onClick={() => bypassInvoice(inv.id)} data-testid={`bypass-${inv.id}`}>
                                          Bypass
                                        </Button>
                                      )}
                                      {inv.status === "bypassed" && (
                                        <Button size="sm" variant="outline" className="h-7 text-xs text-amber-600 border-amber-200"
                                          onClick={() => unbypassInvoice(inv.id)} data-testid={`unbypass-${inv.id}`}>
                                          <ArrowCounterClockwise size={12} className="mr-1" /> {t("fixed_expenses.undo_bypass")}
                                        </Button>
                                      )}
                                      {canDeleteInvoice && (
                                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                                          onClick={() => { setDeleteTarget(inv); setDeleteDialog(true); }}
                                          data-testid={`delete-invoice-${inv.id}`}
                                          title={t("invoices.delete")}>
                                          <Trash size={14} />
                                        </Button>
                                      )}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
              {periods.length > invoicePageSize && (
                <Pagination total={periods.length} page={invoicePage} pageSize={invoicePageSize}
                  onPageChange={setInvoicePage} onPageSizeChange={setInvoicePageSize} />
              )}
              </>
            );
          })()}
        </TabsContent>
      </Tabs>

      {/* ─── Type Dialog ─── */}
      <Dialog open={typeDialog} onOpenChange={setTypeDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingType ? t("fixed_expenses.edit_type") : t("fixed_expenses.new_type_title")}</DialogTitle>
            <DialogDescription>{t("fixed_expenses.define_type")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs">{t("fixed_expenses.type_name")}</Label>
              <Input value={typeName} onChange={e => setTypeName(e.target.value)} placeholder={t("fixed_expenses.type_placeholder")} data-testid="type-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t("common.description")}</Label>
              <Input value={typeDesc} onChange={e => setTypeDesc(e.target.value)} placeholder={t("common.description")} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTypeDialog(false)}>{t("common.cancel")}</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={saveType} disabled={typeSaving} data-testid="save-type-btn">
              {typeSaving ? t("billing.saving") : t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Config Dialog ─── */}
      <Dialog open={configDialog} onOpenChange={setConfigDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("fixed_expenses.config_title")}</DialogTitle>
            <DialogDescription>
              {configApt?.name} - {types.find(t => t.id === configTypeId)?.name || ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs">{t("common.type")}</Label>
              <Select value={configTypeId} onValueChange={v => { setConfigTypeId(v); const c = (configs[configApt?.id] || []).find(x => x.expense_type_id === v); if (c) { setConfigAmount(c.amount); setConfigDueDay(c.due_day); setConfigDueHour(c.due_hour); } }}>
                <SelectTrigger><SelectValue placeholder={t("fixed_expenses.select_type")} /></SelectTrigger>
                <SelectContent>
                  {types.map(expType => <SelectItem key={expType.id} value={expType.id}>{expType.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t("fixed_expenses.amount_all")}</Label>
              <Input type="number" min="0" step="10" value={configAmount}
                onChange={e => setConfigAmount(parseFloat(e.target.value) || 0)} data-testid="config-amount-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">{t("fixed_expenses.due_day")}</Label>
                <Input type="number" min="1" max="28" value={configDueDay}
                  onChange={e => setConfigDueDay(parseInt(e.target.value) || 10)} data-testid="config-due-day" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t("fixed_expenses.cutoff_hour")}</Label>
                <Input type="number" min="0" max="23" value={configDueHour}
                  onChange={e => setConfigDueHour(parseInt(e.target.value) || 8)} data-testid="config-due-hour" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigDialog(false)}>{t("common.cancel")}</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={saveConfig} disabled={configSaving} data-testid="save-config-btn">
              {configSaving ? t("billing.saving") : t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Manual Invoice Dialog ─── */}
      <Dialog open={manualDialog} onOpenChange={setManualDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Tek Fatura Ekle</DialogTitle>
            <DialogDescription>Belirli bir daireye/kiraciya ozel fatura olusturun</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs">{t("sidebar.apartments")}</Label>
              <Select value={manualAptId} onValueChange={onManualAptChange}>
                <SelectTrigger data-testid="manual-apt-select">
                  <SelectValue placeholder="Apartman secin" />
                </SelectTrigger>
                <SelectContent>
                  {apartments.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t("common.unit")}</Label>
              <Select value={manualUnitId} onValueChange={setManualUnitId} disabled={!manualAptId}>
                <SelectTrigger data-testid="manual-unit-select">
                  <SelectValue placeholder="Daire secin" />
                </SelectTrigger>
                <SelectContent>
                  {manualUnits.filter(u => u.tenant_id).map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name} {u.tenant_name ? `(${u.tenant_name})` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Aciklama / Gider Adi</Label>
              <Input value={manualDesc} onChange={e => setManualDesc(e.target.value)}
                placeholder="Ornegin: Tamir, Bakım, Ek masraf..." data-testid="manual-desc-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">{t("common.amount")} (TL)</Label>
                <Input type="number" min="0" step="10" value={manualAmount}
                  onChange={e => setManualAmount(parseFloat(e.target.value) || 0)} data-testid="manual-amount-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t("common.due_date")}</Label>
                <Input type="date" value={manualDueDate}
                  onChange={e => setManualDueDate(e.target.value)} data-testid="manual-due-date" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualDialog(false)}>{t("common.cancel")}</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={saveManualInvoice}
              disabled={manualSaving || !manualUnitId || !manualDesc.trim() || manualAmount <= 0}
              data-testid="save-manual-invoice-btn">
              {manualSaving ? <Spinner size={14} className="animate-spin mr-1" /> : <Plus size={14} className="mr-1" />}
              Fatura Olustur
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Invoice Confirmation */}
      <AlertDialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("invoices.delete_confirm_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && (
                <span>{deleteTarget.tenant_name} - {deleteTarget.expense_type_name} ({deleteTarget.amount?.toFixed(2)} TL)<br/></span>
              )}
              {t("invoices.delete_confirm_desc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteInvoice} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-invoice">
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default FixedExpensesPage;
